import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

/**
 * The "Report a problem" actions on the smartteams-app ticket detail page that are enabled
 * per customer. Stored as an ENABLED-list (opt-in, `enabledProblemReports`): a button is hidden
 * (and rejected server-side) until an admin explicitly turns it on for that customer.
 */
// NB: "Request cancellation" is deliberately NOT here — it's always available to the FE and not
// opt-in configurable, so it's handled separately in the app (never gated by enabledProblemReports).
export const PROBLEM_REPORT_OPTIONS = [
  { key: 'cad', label: 'CAD — Customer arranged a new date' },
  { key: 'cus', label: 'CUS — Visit unsuccessful (Just Go)' },
  { key: 'cna', label: 'CNA — Customer not available' },
  { key: 'cra', label: 'CRA — Refused access at the site' },
  { key: 'tpm', label: 'TPM' },
] as const;

export type ProblemReportKey = (typeof PROBLEM_REPORT_OPTIONS)[number]['key'];

export const PROBLEM_REPORT_KEYS = PROBLEM_REPORT_OPTIONS.map(o => o.key);

/**
 * Master switch for the opt-in "Report a problem" buttons. Currently OFF: every button except
 * "Request cancellation" (which is always-on and handled separately) is disabled product-wide and
 * admins cannot re-enable them. When false: the server treats every customer's enabled-set as empty
 * (so the FE shows none and the server rejects all), and the admin toggles render read-only. The
 * opt-in machinery (`enabledProblemReports`) stays intact so this can be flipped back on later.
 */
export const PROBLEM_REPORTS_ENABLED = false;

@Entity()
export class SmartTeamsCustomer extends MongoEntity {
  static override _class = 'SmartTeamsCustomer';
  static override _collectionName = 'smartteams_customers';

  @validate(nonOptional())
  @Field(() => String)
  name: Maybe<string>;

  /** ServiceNow client sys_id — set via the dropdown in the customer detail page. */
  @Field(() => String)
  serviceNowClientId: Maybe<string>;

  /**
   * "Report a problem" buttons enabled for this customer's tickets (`ProblemReportKey`s), opt-in.
   * Missing/empty = no buttons available — an admin must explicitly enable each one. Enforced
   * server-side in the app's ticket-action mutations, not just hidden in the UI.
   */
  @Field(() => [String])
  enabledProblemReports: Maybe<string[]>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;

  @Field(() => Date)
  archivedAt: Maybe<Date>;

  @Field(() => Date)
  deletedAt: Maybe<Date>;
}
